import { ActivatedRoute, Router } from '@angular/router'
import { CompetitionTeamCreationConfirmationModalComponent } from './../create-team-confirmation-modal/show.component'

import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { competitionTeamSelectors } from './../competition-team.selectors'
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import { UntypedFormGroup, UntypedFormBuilder, NgForm, Validators } from '@angular/forms'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { combineLatest, Observable } from 'rxjs'
import {
  filter,
  first,
  map,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Competition } from '../../competition'
import { CompetitionLevel } from '../../competition-level/competition-level'
import { competitionLevelActions } from '../../competition-level/competition-level.actions'
import { competitionLevelSelectors } from '../../competition-level/competition-level.selectors'
import { competitionActions } from '../../competition.actions'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionTeam } from '../competition-team'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { competitionTeamActions } from '../competition-team.actions'
import { Member } from 'src/app/features/member/member'
import { competitionStaffSelectors } from '../../competition-staff/competition-staff.selectors'
import { competitionPlayerSelectors } from '../../competition-player/competition-player.selectors'
import { Dictionary } from '@ngrx/entity'
import { CompetitionStaff } from '../../competition-staff/competition-staff'
import { CompetitionPlayer } from '../../competition-player/competition-player'
import {
  TranslationField,
  TranslationService,
  TranslationType
} from 'src/app/features/translation/translation.service'
import { translationSelectors } from 'src/app/features/translation/translation.selectors'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { competitionPlayerActions } from '../../competition-player/competition-player.actions'

@UntilDestroy()
@Component({
  selector: 'competition-team-create-form',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CompetitionTeamCreateFormComponent implements OnInit {
  @Input() team: CompetitionTeam
  @Input() showSubmitButton: boolean = true
  @Output() onSubmit = new EventEmitter<any>()

  public teamId$: Observable<number>
  public competitions$: Observable<Competition[]>
  public competitionLevels$: Observable<CompetitionLevel[]>
  public selectedCompetitionId: number
  public bsModalRef: BsModalRef
  public shirtColourRgbLittleEndian: number = null
  public competitionTeamsOfMember$: Observable<CompetitionTeam[]>
  public member$: Observable<Member>
  public team$: Observable<CompetitionTeam>
  public isLoading$: Observable<boolean>
  public competitionEntities$: Observable<Dictionary<Competition>>
  public selectedCompetitionSummary: string

  @ViewChild('formRef') formRef: NgForm

  public form: UntypedFormGroup

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private modalService: BsModalService,
    private router: Router,
    private translationService: TranslationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.isLoading$ = this.store.select(
      competitionTeamSelectors.selectIsLoading
    )
    this.form = this.fb.group({
      description: [null, [Validators.required]],
      competitionId: [null, [Validators.required]],
      competitionLevelId: [null, [Validators.required]],
      shirtColour: ['#FF0000', [Validators.required]]
    })

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )
    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      ),
      first((team) => Boolean(team?.competitionId)),

      map((team) => {
        if (!team) {
          return
        }
        this.team = team

        return {
          ...team,
          shirtColour: this.littleEndianDecimalToRgb(team.shirtColour as number)
        }
      })
    )

    this.store.dispatch(
      competitionActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            startDate: {
              [CondOperator.GREATER_THAN]: moment().toISOString()
            },
            competitionLastRegistrationDate: {
              [CondOperator.GREATER_THAN]: moment().toISOString()
            }
          },
          join: [
            { field: 'linkedChildProduct' },
            { field: 'linkedChildProduct.translations' },
            { field: 'competitionSportId' },
            { field: 'competitionSport' }
          ]
        })
      })
    )

    this.competitions$ = this.store
      .select(competitionSelectors.selectAll)
      .pipe(
        map((competitions) =>
          competitions?.filter(
            (competition) =>
              moment(competition.startDate).isAfter(moment()) &&
              moment(competition.competitionLastRegistrationDate).isAfter(
                moment()
              )
          )
        )
      )

    // if refresh page
    this.competitions$.pipe(untilDestroyed(this)).subscribe((item: any) => {
      if (item.length > 0) {
        this.store.dispatch(
          competitionPlayerActions.upsertMany({ entities: item })
        )
      }
    })

    this.competitionEntities$ = this.store.select(
      competitionSelectors.selectEntities
    )

    this.member$ = this.store.select(authSelectors.selectMember)

    this.member$.pipe(take(1)).subscribe((member) => {
      this.store.dispatch(
        competitionTeamActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              $or: [
                {
                  'competitionPlayers.memberId': member.id
                },
                {
                  'competitionStaff.memberId': member.id
                },
                {
                  teamCaptainPersonId: member.id
                }
              ]
            },
            join: [
              { field: 'competitionPlayers' },
              { field: 'competitionStaff' }
            ]
          })
        })
      )
    })

    combineLatest([
      this.form.controls.competitionId.valueChanges,
      this.competitionEntities$,
      this.store.select(translationSelectors.selectAll),
      this.store.select(settingSelectors.selectById, 'language')
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([competitionId, competitionEntities, translations, language]) => {
          this.selectedCompetitionId = competitionId

          const summary = this.translationService.getTranslation(
            competitionEntities[competitionId]?.linkedChildProductId,
            TranslationType.PRODUCT,
            TranslationField.SUMMARY,
            translations,
            language
          )

          this.selectedCompetitionSummary = summary

          this.store.dispatch(
            competitionLevelActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { competitionId: this.selectedCompetitionId }
              })
            })
          )

          // if there is no selected team
          if (!this.team) {
            combineLatest([
              this.store.select(competitionTeamSelectors.selectAll),
              this.store.select(competitionStaffSelectors.selectEntities),
              this.store.select(competitionPlayerSelectors.selectEntities),
              this.member$
            ])
              .pipe(take(1))
              .subscribe(
                ([
                  competitionTeams,
                  competitionStaffEntities,
                  competitionPlayerEntities,
                  member
                ]) => {
                  let teamsOfMember = competitionTeams?.filter(
                    (competitionTeam) =>
                      competitionTeam?.competitionId ==
                        this.selectedCompetitionId &&
                      competitionTeam.teamCaptainPersonId == member?.id
                  )

                  if (teamsOfMember.length > 0) {
                    this.bsModalRef = this.modalService.show(
                      CompetitionTeamCreationConfirmationModalComponent,
                      {
                        backdrop: 'static',
                        keyboard: false,
                        initialState: { teamsOfMember }
                      }
                    )

                    this.bsModalRef.content.onSubmit
                      .pipe(untilDestroyed(this))
                      .subscribe((value) => {
                        if (!value) {
                          this.router.navigate(['/competitions'])
                        }
                        this.bsModalRef.hide()
                      })
                    return
                  }
                }
              )
          }
        }
      )

    this.competitionLevels$ = this.store
      .select(competitionLevelSelectors.selectAll)
      .pipe(
        map((competitionLevels) =>
          competitionLevels.filter(
            (competitionLevel) =>
              competitionLevel?.competitionId == this.selectedCompetitionId
          )
        ),
        tap((filteredCompetitionLevels) => {
          if (filteredCompetitionLevels?.length == 1) {
            this.form.controls.competitionLevelId.setValue(
              filteredCompetitionLevels[0]?.id
            )
          }
        })
      )

    this.form.controls.shirtColour.valueChanges
      .pipe(startWith('#FF0000'), untilDestroyed(this))
      .subscribe((shirtColour) => {
        // convert shirt colour to 00bbggrr
        this.shirtColourRgbLittleEndian = this.hexToRgbLittleEndian(shirtColour)
      })
  }

  public attemptSubmit(): void {
    Object.keys(this.form.controls).map((controlName) =>
      this.form.controls[controlName].markAsDirty()
    )

    this.formRef.ngSubmit.emit(undefined)
  }

  public onFormSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    const f = this.form.value

    this.member$.pipe(take(1)).subscribe((member) => {
      this.onSubmit.emit({
        ...f,
        competitionId: parseInt(f.competitionId),
        competitionLevelId: parseInt(f.competitionLevelId),
        teamRegistrationDate: moment(),
        shirtColour: this.shirtColourRgbLittleEndian,
        id: this.team ? this.team.id : null,
        teamCaptainPersonId: member.id
      })
    })
  }

  private hexToRgbLittleEndian(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

    const colorRgb = result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null

    return colorRgb ? (colorRgb.b << 16) + (colorRgb.g << 8) + colorRgb.r : null
  }

  private isMemberStaffOfTeam(
    competitionTeam: CompetitionTeam,
    member: Member,
    competitionStaffEntities: Dictionary<CompetitionStaff>
  ): boolean {
    let isStaff = false
    competitionTeam?.competitionStaff?.forEach((staffId) => {
      if (competitionStaffEntities[staffId]?.memberId == member.id) {
        isStaff = true
        return
      }
    })
    return isStaff
  }

  private isMemberPlayerOfTeam(
    competitionTeam: CompetitionTeam,
    member: Member,
    competitionPlayerEntities: Dictionary<CompetitionPlayer>
  ): boolean {
    let isPlayer = false
    competitionTeam?.competitionPlayers?.forEach((playerId) => {
      if (competitionPlayerEntities[playerId]?.memberId == member.id) {
        isPlayer = true
        return
      }
    })
    return isPlayer
  }

  private littleEndianDecimalToRgb(shirtColour: number): string {
    if (!shirtColour) {
      // return default value
      return '#FF0000'
    }
    let hexShirtColour = shirtColour.toString(16)

    const result =
      hexShirtColour.length == 6
        ? hexShirtColour.split('')
        : new Array(6 - hexShirtColour.length)
            .fill('0')
            .concat(hexShirtColour.split(''))

    return `#${hexShirtColour[4]}${hexShirtColour[5]}${hexShirtColour[2]}${hexShirtColour[3]}${hexShirtColour[0]}${hexShirtColour[1]}`
  }
}
